// Information → Instructions (Fernando, 2026-10-01): work instructions written ONCE, as text, in the
// WMS. Every worker reads them here and downloads the PDF — the PDF is generated from the saved
// text on every download (backend/instructions.py), so it is always the current version. Managers
// and admins get a Manager section on the same page: tabs (topics), documents, the text with a live
// preview, Save (a new version), history and restore. Text format: # heading, ## sub-heading,
// - bullet, 1. step, > warning, **bold**. Claude, 2026-10-01.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { authFetch, API } from './api';
import { fmtWh } from './time';

interface Topic { id: number; slug: string; title: string; sort_order: number; active: boolean; docs: number }
interface TrStatus { state: 'ready' | 'updating' | 'failed'; source_version?: number; older_version?: number | null; error?: string | null }
interface DocRow { id: number; title: string; version: number; sort_order: number; active: boolean; updated_by: string | null; updated_at: string; length: number; translations?: Record<string, TrStatus> }
interface TrText { source_version: number; topic_title: string; title: string; body: string; translated_at: string; model: string }
interface LangInfo {
  languages: { code: string; name: string }[]; my_lang: string;
  settings?: { languages: string[]; glossary: Record<string, string> };
  available?: { code: string; name: string }[]; api?: { ready: boolean; reason: string | null };
  calls_last_hour?: number; cap_per_hour?: number; model?: string;
}
interface PageStyle { layout?: 'instruction' | 'form'; corner?: string; size?: 'compact' }
interface Doc extends DocRow { body: string; topic_id: number; topic_title: string; topic_slug: string; page_style?: PageStyle }
interface Version { version: number; title: string; note: string | null; saved_by: string | null; saved_at: string; length: number }
interface Attachment { id: number; filename: string; mime: string; size: number; uploaded_by: string | null; uploaded_at: string }
type Block = [string, any];

const R = `${API}/instructions`;

/** The language this person reads in: the last one chosen on this device, else My profile, else English. */
function preferredLang(info: LangInfo | null): string {
  const codes = (info?.languages || []).map(l => l.code);
  let saved = '';
  try { saved = localStorage.getItem('wms_instr_lang') || ''; } catch { /* */ }
  if (saved && codes.includes(saved)) return saved;
  if (info && codes.includes(info.my_lang)) return info.my_lang;
  return 'en';
}
function rememberLang(code: string) { try { localStorage.setItem('wms_instr_lang', code); } catch { /* */ } }
const langName = (info: LangInfo | null, code: string) => info?.languages.find(l => l.code === code)?.name || code.toUpperCase();

async function jfetch(url: string, init?: RequestInit) {
  const r = await authFetch(url, init);
  const d = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error((d as any).detail || `HTTP ${r.status}`);
  return d;
}

interface TableBlock { header: string[] | null; rows: string[][]; widths: (number | null)[]; fill?: number | null; style?: 'grid' | 'lines' }
// a separator cell may carry the column width: "|--- 8%|--- 30%|---|" (no number = automatic)
// "|===|" instead of "|---|" = the "lines" style; a separator as the FIRST line = widths/style, no header
const SEP_CELL = '\\s*:?[-=]{3,}:?\\s*(?:\\d{1,3}\\s*%)?\\s*';
const TABLE_SEP = new RegExp(`^\\|?${SEP_CELL}(\\|${SEP_CELL})*\\|?$`);
// "*40" repeats a row 40 times; "*fill" repeats it until the PDF page is full (one per table)
const TABLE_REPEAT = /^(\|.*\|)\s*\*\s*(\d{1,3}|fill)$/i;
const FILL_PREVIEW_ROWS = 12;   // on screen a fill row shows this many times
const MAX_TABLE_ROWS = 500;
const tableCells = (line: string) => {
  let t = line.trim();
  if (t.startsWith('|')) t = t.slice(1);
  if (t.endsWith('|')) t = t.slice(0, -1);
  return t.split('|').map(c => c.trim());
};
/** Same as backend _table_block: header from a |---| line under the first row; "*40" repeats a row. */
export function tableBlock(lines: string[]): TableBlock {
  let header: string[] | null = null;
  const rows: string[][] = [];
  const widths: (number | null)[] = [];
  let fill: number | null = null;
  let style: 'grid' | 'lines' = 'grid';
  for (let i = 0; i < lines.length; i++) {
    let ln = lines[i].trim();
    if (TABLE_SEP.test(ln)) {
      if (ln.includes('=')) style = 'lines';
      if (i === 1 && rows.length && header === null) header = rows.pop()!;
      for (const c of tableCells(ln)) { const m = c.match(/(\d{1,3})\s*%/); const n = m ? parseInt(m[1], 10) : 0; widths.push(n > 0 && n <= 100 ? n : null); }
      continue;
    }
    const m = ln.match(TABLE_REPEAT);
    let n = 1;
    if (m && m[2].toLowerCase() === 'fill') { if (fill === null) fill = rows.length; ln = m[1]; }
    else if (m) { n = parseInt(m[2], 10); ln = m[1]; }
    const cells = tableCells(ln);
    for (let k = 0; k < Math.max(1, Math.min(n, MAX_TABLE_ROWS - rows.length)); k++) rows.push([...cells]);
    if (rows.length >= MAX_TABLE_ROWS) break;
  }
  const width = Math.max(0, ...rows.map(r => r.length), header ? header.length : 0);
  const pad = (r: string[]) => [...r, ...Array(width - r.length).fill('')];
  return { header: header ? pad(header) : null, rows: rows.map(pad), widths: [...widths, ...Array(width).fill(null)].slice(0, width), fill, style };
}

const isBox = (c: string) => ['[ ]', '[x]', '[X]'].includes((c || '').trim());
/** a checkbox or a number alone in a cell is centred — the backend's _is_centred */
const isCentred = (c: string) => isBox(c) || /^\**\s*\d{1,5}\s*\**$/.test((c || '').trim());

/** Column widths in % — the same rule as backend table_widths, so the preview is the PDF. */
export function tableWidths(t: TableBlock): number[] {
  const ncol = (t.rows[0] || t.header || []).length;
  const lines = (c: string) => (c || '').replace(/\*\*/g, '').split(/<br\s*\/?>/i).map(x => x.trim());
  const auto: number[] = [];
  for (let i = 0; i < ncol; i++) {
    const body = t.rows.map(r => r[i]);
    const h = t.header ? t.header[i] : '';
    const hWord = Math.max(0, ...lines(h).flatMap(ln => ln.split(/\s+/).map(w => w.length)));
    const hLine = Math.max(0, ...lines(h).map(ln => ln.length));
    const filled = body.filter(c => c.trim() && !isBox(c));
    if (!filled.length && body.some(isBox)) auto.push(Math.max(hWord, 3) + 1);
    else if (!filled.length) auto.push(Math.max(10, Math.min(20, hLine)));
    else auto.push(Math.max(6, hWord, Math.min(42, Math.max(...filled.flatMap(c => lines(c).map(ln => ln.length))))));
  }
  const given = (t.widths || []).slice(0, ncol);
  const fixed = given.reduce((a: number, g) => a + (g || 0), 0);
  const rest = Math.max(0, 100 - fixed);
  const free = auto.reduce((a, x, i) => a + (given[i] ? 0 : x), 0) || 1;
  const out = auto.map((a, i) => (given[i] ? (given[i] as number) : rest * a / free));
  const total = out.reduce((a, b) => a + b, 0) || 1;
  return out.map(x => 100 * x / total);
}

/** Where each table sits in the text (line numbers) — the same grouping as parseBlocks. */
export function tableRanges(body: string): { start: number; end: number }[] {
  const lines = (body || '').split('\n');
  const out: { start: number; end: number }[] = [];
  let i = 0;
  while (i < lines.length) {
    if (lines[i].trim().startsWith('|')) {
      const start = i;
      while (i < lines.length && lines[i].trim().startsWith('|')) i++;
      const tb = tableBlock(lines.slice(start, i).map(x => x.trim()));
      if (tb.rows.length || tb.header) out.push({ start, end: i - 1 });
    } else i++;
  }
  return out;
}

/** The same rules as backend/instructions.parse_blocks — the preview is what the PDF shows. */
export function parseBlocks(body: string): Block[] {
  const blocks: Block[] = [];
  let para: string[] = [];
  let table: string[] = [];
  const flush = () => {
    if (table.length) { const tb = tableBlock(table); if (tb.rows.length || tb.header) blocks.push(['table', tb]); table = []; }
    if (para.length) { blocks.push(['p', para.map(s => s.trim()).join('<br>')]); para = []; }
  };
  for (const raw of (body || '').replace(/\r\n/g, '\n').split('\n')) {
    const s = raw.trim();
    if (s.startsWith('|')) {
      if (para.length) { blocks.push(['p', para.map(x => x.trim()).join('<br>')]); para = []; }
      table.push(s); continue;
    }
    if (table.length) flush();
    if (!s) { flush(); continue; }
    if (/^(-{3,}|={3,})$/.test(s)) { flush(); blocks.push(['rule', s.startsWith('=') ? 'double' : 'single']); continue; }
    const mc = s.match(/^\[([ xX])\]\s+(.*)$/);
    if (mc) {
      flush();
      const item = [mc[1].toLowerCase() === 'x', mc[2].trim()];
      const last = blocks[blocks.length - 1];
      if (last && last[0] === 'check') last[1].push(item); else blocks.push(['check', [item]]);
      continue;
    }
    const mf = s.match(/^(.*?)\s*_{3,}\s*(?:\((.*)\))?\s*$/);
    if (mf && s.includes('___') && !/^([-*•] |#|>)/.test(s) && !/^\d{1,3}[.)]\s+/.test(s)) {
      flush(); blocks.push(['field', { label: mf[1].trim(), note: (mf[2] || '').trim() }]); continue;
    }
    if (s.startsWith('## ')) { flush(); blocks.push(['h2', s.slice(3).trim()]); continue; }
    if (s.startsWith('# ')) { flush(); blocks.push(['h1', s.slice(2).trim()]); continue; }
    if (/^[-*•] /.test(s)) {
      flush();
      const last = blocks[blocks.length - 1];
      if (last && last[0] === 'ul') last[1].push(s.slice(2).trim()); else blocks.push(['ul', [s.slice(2).trim()]]);
      continue;
    }
    const m = s.match(/^(\d{1,3})[.)]\s+(.*)$/);
    if (m) {
      flush();
      const last = blocks[blocks.length - 1];
      // a numbered list keeps its first number ("7. … 8. …" shows 7, 8) — the backend's OList.start
      if (last && last[0] === 'ol') last[1].push(m[2].trim()); else blocks.push(['ol', Object.assign([m[2].trim()], { start: parseInt(m[1], 10) })]);
      continue;
    }
    if (s.startsWith('>')) {
      flush();
      const t = s.slice(1).trim();
      const last = blocks[blocks.length - 1];
      if (last && last[0] === 'warn') last[1] = `${last[1]} ${t}`; else blocks.push(['warn', t]);
      continue;
    }
    para.push(s);
  }
  flush();
  return blocks;
}

function Inline({ text }: { text: string }) {
  const t = (text || '').replace(/\[ \]/g, '☐').replace(/\[[xX]\]/g, '☒');
  const lines = t.split(/<br\s*\/?>/i);
  const bold = (txt: string, k: string) => txt.split(/(\*\*.+?\*\*)/g).map((p, i) => p.startsWith('**') && p.endsWith('**') && p.length > 4
    ? <b key={`${k}${i}`}>{p.slice(2, -2)}</b> : <React.Fragment key={`${k}${i}`}>{p}</React.Fragment>);
  return <>{lines.map((ln, li) => (
    <React.Fragment key={li}>
      {li > 0 && <br />}
      {ln.split(/(<small>[\s\S]*?<\/small>)/gi).map((part, j) => /^<small>/i.test(part)
        ? <small key={j} className="text-[0.75em] text-gray-500">{bold(part.replace(/^<small>|<\/small>$/gi, ''), `s${j}`)}</small>
        : <React.Fragment key={j}>{bold(part, `n${j}`)}</React.Fragment>)}
    </React.Fragment>))}</>;
}

function TableView({ t, onEdit }: { t: TableBlock; onEdit?: () => void }) {
  const w = tableWidths(t);
  return (
    <div className={`my-3 overflow-x-auto ${onEdit ? 'relative cursor-pointer rounded ring-blue-400 hover:ring-2 group' : ''}`} onClick={onEdit}
      title={onEdit ? 'Click to edit this table' : undefined}>
      {onEdit && <span className="absolute right-1 top-1 z-10 hidden group-hover:block bg-blue-600 text-white text-xs px-2 py-0.5 rounded">✎ Edit table</span>}
      <table className="border-collapse text-[13px] w-full table-fixed">
        <colgroup>{w.map((x, i) => <col key={i} style={{ width: `${x}%` }} />)}</colgroup>
        {t.header && <thead><tr>{t.header.map((c, i) => <th key={i} className={`border border-gray-500 bg-gray-100 px-2 py-1 text-left align-top ${c.includes('**') ? 'font-normal' : 'font-bold'}`}><Inline text={c} /></th>)}</tr></thead>}
        <tbody>{(t.fill != null ? [...t.rows.slice(0, t.fill), ...Array(FILL_PREVIEW_ROWS).fill(t.rows[t.fill]), ...t.rows.slice(t.fill + 1)] : t.rows).map((r: string[], ri: number) => {
          const blank = r.every(c => !c.trim() || isBox(c));
          return <tr key={ri} style={blank ? { height: '1.9rem' } : undefined}>{r.map((c, i) =>
            <td key={i} className={`${t.style === 'lines' ? 'border-b border-gray-700' : 'border border-gray-500'} px-2 py-1 align-top ${isBox(c) ? 'text-center text-base' : isCentred(c) ? 'text-center' : ''}`}><Inline text={c} /></td>)}</tr>;
        })}</tbody>
      </table>
      {t.fill != null && <div className="text-[11px] text-gray-500 italic mt-1">↓ In the PDF these rows continue to the end of the page.</div>}
    </div>
  );
}

/** The instruction as it reads on screen (and as the PDF lays it out). */
export function DocView({ title, body, light, onTableClick, pageStyle }: { title?: string; body: string; light?: boolean; onTableClick?: (index: number) => void; pageStyle?: PageStyle }) {
  const form = pageStyle?.layout === 'form';
  const compact = pageStyle?.size === 'compact';
  const corner = form ? (pageStyle?.corner ?? 'MOTLEY DENIM') : '';
  const blocks = useMemo(() => parseBlocks(body), [body]);
  let tableNo = -1;
  const txt = light ? 'text-gray-900' : 'text-gray-100';
  return (
    <div className={`${txt} ${compact ? 'text-[13px] leading-snug' : 'text-[15px] leading-relaxed'}`}>
      {title && !form && <h1 className="text-2xl font-bold mb-3">{title}</h1>}
      {title && form && (
        <div className="mb-4">
          <h1 className="text-[1.7rem] text-center leading-tight pb-1 border-b border-current">{title}</h1>
          {corner && <div className="text-right text-[9px] font-bold tracking-wide mt-1">{corner}</div>}
        </div>)}
      {blocks.length === 0 && <div className="text-gray-500 italic">No text yet.</div>}
      {blocks.map(([k, v], i) => {
        if (k === 'h1') return <h2 key={i} className="text-xl font-bold mt-5 mb-2"><Inline text={v} /></h2>;
        if (k === 'h2') return <h3 key={i} className="text-base font-bold mt-4 mb-1.5"><Inline text={v} /></h3>;
        if (k === 'p') return <p key={i} className="mb-2.5"><Inline text={v} /></p>;
        if (k === 'ul') return <ul key={i} className="list-disc pl-6 mb-2.5 space-y-0.5">{v.map((x: string, j: number) => <li key={j}><Inline text={x} /></li>)}</ul>;
        if (k === 'ol') return <ol key={i} start={(v as any).start || 1} className="list-decimal pl-6 mb-2.5 space-y-0.5 marker:font-bold">{v.map((x: string, j: number) => <li key={j}><Inline text={x} /></li>)}</ol>;
        if (k === 'rule') return v === 'double'
          ? <div key={i} className="my-3 border-t border-b border-current h-[3px]" />
          : <hr key={i} className="my-3 border-current" />;
        if (k === 'check') return (
          <div key={i} className="mb-2.5 space-y-0.5">{v.map(([done, x]: [boolean, string], j: number) =>
            <div key={j} className="flex gap-2"><span className="w-4 shrink-0">{done ? '☒' : '☐'}</span><span><Inline text={x} /></span></div>)}</div>);
        if (k === 'field') return (
          <div key={i} className="flex items-end gap-2 mb-3 mt-1">
            {v.label && <b className="whitespace-nowrap"><Inline text={v.label} /></b>}
            <span className="flex-1 border-b border-current min-w-[4rem] h-5" />
            {v.note && <small className="text-[0.75em] text-gray-500 whitespace-nowrap">{v.note}</small>}
          </div>);
        if (k === 'table') { tableNo += 1; const n = tableNo; return <TableView key={i} t={v} onEdit={onTableClick ? () => onTableClick(n) : undefined} />; }
        if (k === 'warn') return (
          <div key={i} className="my-3 flex gap-3 rounded-lg border border-amber-500 bg-amber-100 text-amber-950 px-3 py-2">
            <span className="font-black">!</span><span><Inline text={v} /></span>
          </div>);
        return null;
      })}
    </div>
  );
}

function downloadPdf(url: string, filename: string, onErr: (m: string) => void) {
  const token = localStorage.getItem('wms_token') || '';
  fetch(url, { headers: { Authorization: `Bearer ${token}` } }).then(async res => {
    if (!res.ok) { onErr('The PDF could not be made'); return; }
    const blob = await res.blob();
    const cd = res.headers.get('content-disposition') || '';
    const m = cd.match(/filename="([^"]+)"/);
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = m ? m[1] : filename;
    a.click();
    setTimeout(() => URL.revokeObjectURL(a.href), 2000);
  }).catch(() => onErr('The PDF could not be made'));
}

const FORMAT_HELP = (
  <div className="text-xs text-gray-400 space-y-0.5">
    <div><code className="text-gray-200"># Heading</code> · <code className="text-gray-200">## Sub-heading</code></div>
    <div><code className="text-gray-200">- item</code> bullet · <code className="text-gray-200">1. step</code> numbered</div>
    <div><code className="text-gray-200">&gt; Never do X</code> yellow warning box · <code className="text-gray-200">**bold**</code></div>
    <div>An empty line starts a new paragraph.</div>
    <div>The buttons above write all of this for you. <b className="text-gray-200">Tables:</b> ▦ Table, and click a table in the preview to edit it.</div>
    <div><code className="text-gray-200">Date: ___ (note)</code> fill-in line · <code className="text-gray-200">[ ] item</code> checklist · <code className="text-gray-200">---</code> / <code className="text-gray-200">===</code> divider · <code className="text-gray-200">&lt;small&gt;…&lt;/small&gt;</code> small text · lines one under the other stay separate lines</div>
    <div>In text:</div>
    <div><code className="text-gray-200">| Moved | Article | Old place |</code> a table row · <code className="text-gray-200">|---|---|---|</code> under the first row makes it the header (repeats on every PDF page)</div>
    <div><code className="text-gray-200">[ ]</code> ☐ · <code className="text-gray-200">[x]</code> ☒ · <code className="text-gray-200">&lt;br&gt;</code> new line in a cell · <code className="text-gray-200">| [ ] | | | *40</code> = that row 40 times · <code className="text-gray-200">*fill</code> = repeated to the end of the page</div>
  </div>
);

export default function InstructionsPage() {
  const { slug } = useParams<{ slug?: string }>();
  const navigate = useNavigate();
  const [topics, setTopics] = useState<Topic[] | null>(null);
  const [canEdit, setCanEdit] = useState(false);
  const [manager, setManager] = useState<boolean>(() => { try { return localStorage.getItem('wms_instr_manager') === '1'; } catch { return false; } });
  const [docs, setDocs] = useState<DocRow[]>([]);
  const [open, setOpen] = useState<Doc | null>(null);
  const [err, setErr] = useState('');
  const [langs, setLangs] = useState<LangInfo | null>(null);
  const loadLangs = useCallback(() => jfetch(`${R}/languages`).then(setLangs).catch(e => setErr(e.message)), []);
  useEffect(() => { loadLangs(); }, [loadLangs]);

  const loadTopics = useCallback(() => jfetch(`${R}/topics?include_archived=${manager}`).then(d => { setTopics(d.topics); setCanEdit(d.can_edit); }).catch(e => setErr(e.message)), [manager]);
  useEffect(() => { loadTopics(); }, [loadTopics]);
  const current = topics?.find(t => t.slug === slug) || null;
  useEffect(() => {
    if (topics && !slug && topics.length) navigate(`/information/instructions/${topics.find(t => t.active)?.slug || topics[0].slug}`, { replace: true });
  }, [topics, slug, navigate]);
  const loadDocs = useCallback(() => {
    if (!slug) return;
    jfetch(`${R}/topics/${slug}?include_archived=${manager}`).then(d => setDocs(d.docs)).catch(e => setErr(e.message));
  }, [slug, manager]);
  useEffect(() => { setOpen(null); loadDocs(); }, [loadDocs]);
  const setMgr = (v: boolean) => { setManager(v); try { localStorage.setItem('wms_instr_manager', v ? '1' : '0'); } catch { /* */ } };
  const openDoc = (id: number) => jfetch(`${R}/docs/${id}`).then(setOpen).catch(e => setErr(e.message));

  return (
    <div className="p-4 sm:p-6 max-w-6xl">
      <div className="flex flex-wrap items-baseline justify-between gap-3 mb-1">
        <h1 className="text-2xl font-bold text-white">Instructions</h1>
        {canEdit && (
          <label className="flex items-center gap-2 text-sm text-gray-300 cursor-pointer">
            <input type="checkbox" checked={manager} onChange={e => setMgr(e.target.checked)} /> Manager section
          </label>
        )}
      </div>
      <p className="text-gray-500 text-sm mb-4">How we do the work. Read here or download the PDF — the PDF is always the latest saved version.</p>
      {err && <div className="text-red-400 text-sm mb-3">{err} <button className="underline" onClick={() => setErr('')}>ok</button></div>}

      <div className="flex gap-1 border-b border-gray-800 mb-5 overflow-x-auto">
        {(topics || []).map(t => (
          <button key={t.id} onClick={() => navigate(`/information/instructions/${t.slug}`)}
            className={`px-4 py-2 text-sm font-medium rounded-t-lg whitespace-nowrap ${t.slug === slug ? 'text-white border-b-2 border-blue-500' : 'text-gray-400 hover:text-white'} ${t.active ? '' : 'italic opacity-60'}`}>
            {t.title}{!t.active && ' (archived)'}
          </button>
        ))}
        {topics && topics.length === 0 && <div className="text-gray-500 text-sm py-2">No instructions yet.{canEdit ? ' Tick "Manager section" to add the first tab.' : ''}</div>}
      </div>

      {manager && canEdit && <TopicManager topics={topics || []} current={current} onChange={(s?: string) => { loadTopics(); if (s) navigate(`/information/instructions/${s}`); }} onErr={setErr} />}
      {manager && canEdit && langs?.settings && <LanguagesManager info={langs} onSaved={() => { loadLangs(); loadDocs(); }} onErr={setErr} />}

      {current && (open
        ? <DocPanel doc={open} manager={manager && canEdit} topics={topics || []} langs={langs} onClose={() => { setOpen(null); loadDocs(); }}
            onSaved={d => { setOpen(d); loadDocs(); }} onErr={setErr} />
        : <DocList docs={docs} manager={manager && canEdit} topic={current} langs={langs} onOpen={openDoc} onChange={loadDocs} onErr={setErr} />)}
    </div>
  );
}

function DocList({ docs, manager, topic, langs, onOpen, onChange, onErr }: { docs: DocRow[]; manager: boolean; topic: Topic; langs: LangInfo | null; onOpen: (id: number) => void; onChange: () => void; onErr: (m: string) => void }) {
  const [adding, setAdding] = useState('');
  const [choosing, setChoosing] = useState<DocRow | null>(null);
  const add = async () => {
    try {
      const d = await jfetch(`${R}/docs`, { method: 'POST', body: JSON.stringify({ topic_id: topic.id, title: adding.trim(), body: '' }) });
      setAdding(''); onChange(); onOpen(d.id);
    } catch (e: any) { onErr(e.message); }
  };
  const move = async (d: DocRow, dir: -1 | 1) => {
    const live = docs.filter(x => x.active);
    const i = live.findIndex(x => x.id === d.id), j = i + dir;
    if (j < 0 || j >= live.length) return;
    try {
      await jfetch(`${R}/docs/${d.id}`, { method: 'PUT', body: JSON.stringify({ sort_order: live[j].sort_order }) });
      await jfetch(`${R}/docs/${live[j].id}`, { method: 'PUT', body: JSON.stringify({ sort_order: d.sort_order }) });
      onChange();
    } catch (e: any) { onErr(e.message); }
  };
  return (
    <div className="space-y-2">
      {docs.length === 0 && <div className="text-gray-500 text-sm">No documents in this tab yet.</div>}
      {docs.map(d => (
        <div key={d.id} className={`flex flex-wrap items-center gap-3 bg-[#1a1d27] border border-gray-800 rounded-xl px-4 py-3 ${d.active ? '' : 'opacity-60'}`}>
          <button onClick={() => onOpen(d.id)} className="flex-1 min-w-[12rem] text-left">
            <div className="text-white font-semibold">{d.title}{!d.active && <span className="text-xs text-gray-400 font-normal"> · archived</span>}</div>
            <div className="text-xs text-gray-500">Version {d.version} · updated {fmtWh(d.updated_at, 'date')}{d.updated_by ? ` by ${d.updated_by}` : ''}</div>
            <TrChips doc={d} />
          </button>
          {manager && d.active && <span className="flex gap-1 text-gray-500"><button onClick={() => move(d, -1)} className="hover:text-white px-1">▲</button><button onClick={() => move(d, 1)} className="hover:text-white px-1">▼</button></span>}
          <button onClick={() => onOpen(d.id)} className="px-3 py-1.5 rounded-lg bg-gray-700 hover:bg-gray-600 text-white text-sm">{manager ? 'Open / edit' : 'Read'}</button>
          <button onClick={() => setChoosing(d)} className="px-3 py-1.5 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm">Download PDF</button>
        </div>
      ))}
      {choosing && <DownloadChooser doc={choosing} langs={langs} onClose={() => setChoosing(null)} onErr={onErr} />}
      {manager && (
        <div className="flex gap-2 pt-2">
          <input value={adding} onChange={e => setAdding(e.target.value)} placeholder={`New document in "${topic.title}" — its title`}
            onKeyDown={e => { if (e.key === 'Enter' && adding.trim()) add(); }}
            className="flex-1 bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white" />
          <button disabled={!adding.trim()} onClick={add} className="px-4 py-2 rounded-lg bg-emerald-700 hover:bg-emerald-600 disabled:opacity-40 text-white text-sm">+ Add document</button>
        </div>
      )}
    </div>
  );
}

function DocPanel({ doc, manager, topics, langs, onClose, onSaved, onErr }: { doc: Doc; manager: boolean; topics: Topic[]; langs: LangInfo | null; onClose: () => void; onSaved: (d: Doc) => void; onErr: (m: string) => void }) {
  const [choosing, setChoosing] = useState(false);
  const [title, setTitle] = useState(doc.title);
  const [body, setBody] = useState(doc.body);
  const bodyRef = React.useRef<HTMLTextAreaElement>(null);
  // the table editor: a new table goes in at the cursor; a clicked table replaces its own lines
  const [tableEdit, setTableEdit] = useState<{ model: TModel; range?: { start: number; end: number }; at?: number } | null>(null);
  const newTable = () => setTableEdit({ model: newModel(), at: bodyRef.current ? bodyRef.current.selectionStart : body.length });
  const editTable = (n: number) => {
    const r = tableRanges(body)[n];
    if (!r) return;
    setTableEdit({ model: modelFromLines(body.split('\n').slice(r.start, r.end + 1)), range: r });
  };
  const applyTable = (m: TModel | null) => {
    if (!tableEdit) return;
    const text = m ? modelToLines(m).join('\n') : '';
    if (tableEdit.range) {
      const lines = body.split('\n');
      const { start, end } = tableEdit.range;
      setBody([...lines.slice(0, start), ...(text ? [text] : []), ...lines.slice(end + 1)].join('\n'));
    } else if (text) {
      const at = Math.min(tableEdit.at ?? body.length, body.length);
      const before = body.slice(0, at), after = body.slice(at);
      const pre = before && !before.endsWith('\n\n') ? (before.endsWith('\n') ? '\n' : '\n\n') : '';
      setBody(before + pre + text + '\n' + (after.startsWith('\n') ? '' : '\n') + after);
    }
    setTableEdit(null);
  };
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const [history, setHistory] = useState<Version[] | null>(null);
  const [oldView, setOldView] = useState<{ v: Version; body: string } | null>(null);
  const psOf = (d: Doc): PageStyle => ({
    ...(d.page_style?.layout === 'form' ? { layout: 'form', corner: d.page_style.corner ?? 'MOTLEY DENIM' } : { layout: 'instruction' }),
    ...(d.page_style?.size === 'compact' ? { size: 'compact' } : {}),
  } as PageStyle);
  const [pageStyle, setPageStyle] = useState<PageStyle>(() => psOf(doc));
  useEffect(() => { setTitle(doc.title); setBody(doc.body); setNote(''); setPageStyle(psOf(doc)); }, [doc]);
  const dirty = title !== doc.title || body !== doc.body || JSON.stringify(pageStyle) !== JSON.stringify(psOf(doc));

  const save = async () => {
    setBusy(true); setMsg('');
    try {
      await jfetch(`${R}/docs/${doc.id}`, { method: 'PUT', body: JSON.stringify({ title, body, page_style: pageStyle, note: note.trim() || null, base_version: doc.version }) });
      const fresh = await jfetch(`${R}/docs/${doc.id}`);
      onSaved(fresh); setMsg(`Saved as version ${fresh.version} — every download from now on is this text.`);
      setHistory(null);
    } catch (e: any) { setMsg(e.message); } finally { setBusy(false); }
  };
  const loadHistory = () => jfetch(`${R}/docs/${doc.id}/versions`).then(d => setHistory(d.versions)).catch(e => onErr(e.message));
  const viewOld = async (v: Version) => { try { const d = await jfetch(`${R}/docs/${doc.id}/versions/${v.version}`); setOldView({ v, body: d.body }); } catch (e: any) { onErr(e.message); } };
  const restore = async (v: Version) => {
    if (!window.confirm(`Make version ${v.version} (${fmtWh(v.saved_at, 'date')}, ${v.saved_by || '?'}) the current text again? It is saved as a new version; nothing is lost.`)) return;
    try { await jfetch(`${R}/docs/${doc.id}/restore/${v.version}`, { method: 'POST', body: '{}' }); const fresh = await jfetch(`${R}/docs/${doc.id}`); onSaved(fresh); setOldView(null); loadHistory(); setMsg(`Version ${v.version} restored as version ${fresh.version}.`); }
    catch (e: any) { onErr(e.message); }
  };
  const archive = async (active: boolean) => {
    if (!active && !window.confirm(`Archive "${doc.title}"? Workers stop seeing it; it stays in the Manager section and can be brought back.`)) return;
    try { await jfetch(`${R}/docs/${doc.id}`, { method: 'PUT', body: JSON.stringify({ active }) }); onClose(); } catch (e: any) { onErr(e.message); }
  };
  const moveTo = async (topic_id: number) => {
    try { await jfetch(`${R}/docs/${doc.id}`, { method: 'PUT', body: JSON.stringify({ topic_id }) }); onClose(); } catch (e: any) { onErr(e.message); }
  };
  const previewPdf = async () => {
    const token = localStorage.getItem('wms_token') || '';
    const r = await fetch(`${R}/preview.pdf`, { method: 'POST', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }, body: JSON.stringify({ title, body, page_style: pageStyle }) });
    if (!r.ok) { onErr('The preview PDF could not be made'); return; }
    window.open(URL.createObjectURL(await r.blob()), '_blank');
  };

  return (
    <div>
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <button onClick={() => { if (!dirty || window.confirm('Leave without saving your changes?')) onClose(); }} className="text-gray-400 hover:text-white text-sm">← {doc.topic_title}</button>
        <span className="flex-1" />
        <span className="text-xs text-gray-500">Version {doc.version} · updated {fmtWh(doc.updated_at, 'datetime')}{doc.updated_by ? ` by ${doc.updated_by}` : ''}</span>
        <button onClick={() => setChoosing(true)} className="px-3 py-1.5 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm">Download PDF</button>
      </div>
      {choosing && <DownloadChooser doc={doc} langs={langs} onClose={() => setChoosing(false)} onErr={onErr} />}
      <Attachments docId={doc.id} manager={manager} onErr={onErr} />

      {!manager ? (
        <ReadView doc={doc} langs={langs} />
      ) : (
        <>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="space-y-2">
              <input value={title} onChange={e => setTitle(e.target.value)} className="w-full bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-white font-semibold" />
              <div className="flex flex-wrap items-center gap-2 text-xs text-gray-300">
                <label className="flex items-center gap-1">Page style
                  <select value={pageStyle.layout} onChange={e => setPageStyle({ ...(e.target.value === 'form' ? { layout: 'form', corner: pageStyle.corner ?? 'MOTLEY DENIM' } : { layout: 'instruction' }), ...(pageStyle.size ? { size: pageStyle.size } : {}) } as PageStyle)}
                    className="bg-gray-800 border border-gray-600 rounded px-1 py-0.5 text-white">
                    <option value="instruction">Instruction — title on the left</option>
                    <option value="form">Form — title centred with a line, name at the top right</option>
                  </select></label>
                {pageStyle.layout === 'form' && <label className="flex items-center gap-1">top right
                  <input value={pageStyle.corner ?? ''} onChange={e => setPageStyle({ ...pageStyle, corner: e.target.value })} placeholder="(nothing)"
                    className="w-36 bg-gray-900 border border-gray-700 rounded px-1.5 py-0.5 text-white" /></label>}
                <label className="flex items-center gap-1" title="Compact = smaller text and spacing, to fit a long table on one page">Text size
                  <select value={pageStyle.size || 'normal'} onChange={e => { const { size, ...rest } = pageStyle; void size; setPageStyle(e.target.value === 'compact' ? { ...rest, size: 'compact' } : rest); }}
                    className="bg-gray-800 border border-gray-600 rounded px-1 py-0.5 text-white">
                    <option value="normal">Normal</option>
                    <option value="compact">Compact — fits more on a page</option>
                  </select></label>
              </div>
              <EditorToolbar area={bodyRef} body={body} setBody={setBody} onTable={newTable} />
              {tableEdit && <TableEditor initial={tableEdit.model} isNew={!tableEdit.range} onApply={applyTable} onCancel={() => setTableEdit(null)} />}
              <textarea ref={bodyRef} value={body} onChange={e => setBody(e.target.value)} rows={24} spellCheck
                className="w-full bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-gray-100 font-mono text-[13px] leading-relaxed"
                placeholder={'# Before you start\nTake the **big cart**.\n\n## Steps\n1. Open the picking list.\n2. …\n\n> Never put two orders in one box.'} />
              {FORMAT_HELP}
            </div>
            <div>
              <div className="text-xs text-gray-500 uppercase tracking-wider mb-1">Preview — as the PDF shows it</div>
              <div className="bg-white rounded-xl p-5 max-h-[38rem] overflow-auto"><DocView title={title} body={body} light onTableClick={editTable} pageStyle={pageStyle} /></div>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2 mt-3">
            <input value={note} onChange={e => setNote(e.target.value)} placeholder="What changed (optional, shown in the history)"
              className="flex-1 min-w-[14rem] bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white" />
            <button disabled={busy || !dirty} onClick={save} className="px-5 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-500 disabled:opacity-40 text-white text-sm font-semibold">{busy ? 'Saving…' : dirty ? 'Save' : 'Saved'}</button>
            <button onClick={previewPdf} className="px-3 py-2 rounded-lg bg-gray-700 hover:bg-gray-600 text-white text-sm">PDF preview</button>
            <button onClick={() => (history ? setHistory(null) : loadHistory())} className="px-3 py-2 rounded-lg bg-gray-700 hover:bg-gray-600 text-white text-sm">{history ? 'Hide history' : 'History'}</button>
            <select value="" onChange={e => e.target.value && moveTo(Number(e.target.value))} className="bg-gray-800 border border-gray-700 rounded-lg px-2 py-2 text-sm text-gray-300">
              <option value="">Move to tab…</option>
              {topics.filter(t => t.active && t.id !== doc.topic_id).map(t => <option key={t.id} value={t.id}>{t.title}</option>)}
            </select>
            {doc.active
              ? <button onClick={() => archive(false)} className="px-3 py-2 rounded-lg text-red-300 hover:bg-red-950/40 text-sm">Archive</button>
              : <button onClick={() => archive(true)} className="px-3 py-2 rounded-lg text-emerald-300 hover:bg-emerald-950/40 text-sm">Bring back</button>}
          </div>
          {msg && <div className={`text-sm mt-2 ${msg.startsWith('Saved') || msg.includes('restored') ? 'text-emerald-300' : 'text-amber-300'}`}>{msg}</div>}
          <A4View title={title} body={body} pageStyle={pageStyle} />
          <TranslationsBox doc={doc} langs={langs} onErr={onErr} />
          {history && (
            <div className="mt-4 bg-[#1a1d27] border border-gray-800 rounded-xl p-4">
              <div className="text-white font-semibold text-sm mb-2">History</div>
              <div className="space-y-1 text-sm">
                {history.map(v => (
                  <div key={v.version} className="flex flex-wrap items-center gap-3 text-gray-300">
                    <span className="w-14 font-semibold">v{v.version}</span>
                    <span className="text-gray-400">{fmtWh(v.saved_at, 'datetime')} · {v.saved_by || '?'}</span>
                    <span className="flex-1 text-gray-500">{v.note || ''}</span>
                    <button onClick={() => viewOld(v)} className="text-blue-300 text-xs hover:underline">view</button>
                    <button onClick={() => downloadPdf(`${R}/docs/${doc.id}/versions/${v.version}?pdf=true`, `${doc.title}-v${v.version}.pdf`, onErr)} className="text-blue-300 text-xs hover:underline">PDF</button>
                    {v.version !== doc.version && <button onClick={() => restore(v)} className="text-amber-300 text-xs hover:underline">restore</button>}
                  </div>
                ))}
              </div>
              {oldView && (
                <div className="mt-3">
                  <div className="text-xs text-gray-500 mb-1">Version {oldView.v.version} — {fmtWh(oldView.v.saved_at, 'datetime')} by {oldView.v.saved_by} <button className="underline ml-2" onClick={() => setOldView(null)}>close</button></div>
                  <div className="bg-white rounded-xl p-5 max-h-96 overflow-auto"><DocView title={oldView.v.title} body={oldView.body} light /></div>
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}

function TopicManager({ topics, current, onChange, onErr }: { topics: Topic[]; current: Topic | null; onChange: (slug?: string) => void; onErr: (m: string) => void }) {
  const [name, setName] = useState('');
  const [rename, setRename] = useState('');
  useEffect(() => { setRename(current?.title || ''); }, [current?.id, current?.title]);
  const add = async () => {
    try { const t = await jfetch(`${R}/topics`, { method: 'POST', body: JSON.stringify({ title: name.trim() }) }); setName(''); onChange(t.slug); }
    catch (e: any) { onErr(e.message); }
  };
  const update = async (body: any) => {
    if (!current) return;
    try { await jfetch(`${R}/topics/${current.id}`, { method: 'PUT', body: JSON.stringify(body) }); onChange(); } catch (e: any) { onErr(e.message); }
  };
  const move = async (dir: -1 | 1) => {
    if (!current) return;
    const live = topics.filter(t => t.active);
    const i = live.findIndex(t => t.id === current.id), j = i + dir;
    if (j < 0 || j >= live.length) return;
    try {
      await jfetch(`${R}/topics/${current.id}`, { method: 'PUT', body: JSON.stringify({ sort_order: live[j].sort_order }) });
      await jfetch(`${R}/topics/${live[j].id}`, { method: 'PUT', body: JSON.stringify({ sort_order: current.sort_order }) });
      onChange();
    } catch (e: any) { onErr(e.message); }
  };
  return (
    <div className="mb-5 rounded-xl border border-emerald-900/60 bg-emerald-950/20 p-3 flex flex-wrap items-center gap-2 text-sm">
      <span className="text-emerald-300 font-semibold mr-1">Manager section</span>
      <input value={name} onChange={e => setName(e.target.value)} onKeyDown={e => { if (e.key === 'Enter' && name.trim()) add(); }}
        placeholder="New tab, e.g. Packing" className="bg-gray-900 border border-gray-700 rounded-lg px-3 py-1.5 text-white w-48" />
      <button disabled={!name.trim()} onClick={add} className="px-3 py-1.5 rounded-lg bg-emerald-700 hover:bg-emerald-600 disabled:opacity-40 text-white">+ Add tab</button>
      {current && <>
        <span className="text-gray-600 mx-1">|</span>
        <span className="text-gray-400">This tab:</span>
        <input value={rename} onChange={e => setRename(e.target.value)} className="bg-gray-900 border border-gray-700 rounded-lg px-3 py-1.5 text-white w-44" />
        <button disabled={!rename.trim() || rename === current.title} onClick={() => update({ title: rename.trim() })} className="px-2 py-1.5 rounded-lg bg-gray-700 disabled:opacity-40 text-white">Rename</button>
        <button onClick={() => move(-1)} className="px-2 py-1.5 rounded-lg bg-gray-700 text-white" title="Move left">◀</button>
        <button onClick={() => move(1)} className="px-2 py-1.5 rounded-lg bg-gray-700 text-white" title="Move right">▶</button>
        {current.active
          ? <button onClick={() => { if (window.confirm(`Archive the tab "${current.title}"? Workers stop seeing it and its documents.`)) update({ active: false }); }} className="px-2 py-1.5 rounded-lg text-red-300 hover:bg-red-950/40">Archive tab</button>
          : <button onClick={() => update({ active: true })} className="px-2 py-1.5 rounded-lg text-emerald-300 hover:bg-emerald-950/40">Bring tab back</button>}
      </>}
    </div>
  );
}

// ─── languages (2026-10-01): English is written by managers, Claude translates each saved version once ──

interface TrView extends TrStatus { lang: string; name: string; current: TrText | null; older: TrText | null }

/** The translation state of one document in every enabled language — read from the stored rows only. */
function useTrViews(docId: number, version: number, langs: LangInfo | null, poll = false) {
  const [views, setViews] = useState<Record<string, TrView>>({});
  const codes = useMemo(() => (langs?.languages || []).map(l => l.code).filter(c => c !== 'en'), [langs]);
  const load = useCallback(() => {
    Promise.all(codes.map(c => jfetch(`${R}/docs/${docId}?lang=${c}`).then(d => [c, d.translation] as [string, TrView])))
      .then(rows => setViews(Object.fromEntries(rows))).catch(() => { /* shown as unknown */ });
  }, [docId, codes]);
  useEffect(() => { load(); }, [load, version]);
  const updating = Object.values(views).some(v => v.state === 'updating');
  useEffect(() => {
    if (!poll || !updating) return;
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, [poll, updating, load]);
  return { views, reload: load, codes };
}

function TrChips({ doc }: { doc: DocRow }) {
  const tr = doc.translations || {};
  const codes = Object.keys(tr);
  if (!codes.length) return null;
  return (
    <div className="flex flex-wrap gap-1.5 mt-1">
      {codes.map(c => {
        const t = tr[c];
        const cls = t.state === 'ready' ? 'bg-emerald-900/40 text-emerald-300' : t.state === 'failed' ? 'bg-red-900/40 text-red-300' : 'bg-amber-900/40 text-amber-300';
        const txt = t.state === 'ready' ? `✓ v${t.source_version}` : t.state === 'failed' ? 'translation failed' : 'translating…';
        return <span key={c} title={t.error || ''} className={`text-[11px] px-1.5 py-0.5 rounded ${cls}`}>{c.toUpperCase()} {txt}</span>;
      })}
    </div>
  );
}

/** Files a document carries that cannot be text (30.10: the scanner programming barcodes). Everyone downloads;
 *  a manager adds (PDF, PNG, JPEG, text) and removes. The text stays the source of the generated PDF. */
function Attachments({ docId, manager, onErr }: { docId: number; manager: boolean; onErr: (m: string) => void }) {
  const [files, setFiles] = useState<Attachment[] | null>(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { jfetch(`${R}/docs/${docId}/files`).then(d => setFiles(d.files || [])).catch(() => setFiles([])); }, [docId]);
  const add = (f: File) => {
    if (f.size > 10 * 1024 * 1024) { onErr('At most 10 MB per file'); return; }
    const rd = new FileReader();
    rd.onload = async () => {
      setBusy(true);
      try {
        const b64 = String(rd.result).split(',')[1] || '';
        const d = await jfetch(`${R}/docs/${docId}/files`, { method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ filename: f.name, mime: f.type || 'application/octet-stream', data_b64: b64 }) });
        setFiles(d.files || []);
      } catch (e: any) { onErr(e.message); } finally { setBusy(false); }
    };
    rd.readAsDataURL(f);
  };
  const remove = async (a: Attachment) => {
    if (!window.confirm(`Remove the file "${a.filename}"?`)) return;
    try { const d = await jfetch(`${R}/files/${a.id}`, { method: 'DELETE' }); setFiles(d.files || []); } catch (e: any) { onErr(e.message); }
  };
  if (!files || (files.length === 0 && !manager)) return null;
  const kb = (n: number) => n >= 1024 * 1024 ? `${(n / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`;
  return (
    <div className="mb-4 rounded-xl border border-gray-800 bg-gray-900/40 px-3 py-2">
      <div className="text-xs text-gray-500 uppercase tracking-wider mb-1">Attached files</div>
      <div className="flex flex-wrap gap-2 items-center">
        {files.map(a => (
          <span key={a.id} className="inline-flex items-center gap-2 bg-gray-800 rounded-lg px-3 py-1.5 text-sm">
            <button onClick={() => downloadPdf(`${R}/files/${a.id}`, a.filename, onErr)} className="text-blue-300 hover:underline font-medium" title={`${kb(a.size)} · ${a.uploaded_by || ''} ${fmtWh(a.uploaded_at, 'datetime')}`}>⬇ {a.filename}</button>
            <span className="text-gray-500 text-xs">{kb(a.size)}</span>
            {manager && <button onClick={() => remove(a)} className="text-gray-500 hover:text-red-300 text-xs" title="remove this file">✕</button>}
          </span>
        ))}
        {manager && (
          <label className={`text-sm px-3 py-1.5 rounded-lg bg-gray-700 hover:bg-gray-600 text-white cursor-pointer ${busy ? 'opacity-50' : ''}`}>
            {busy ? 'Uploading…' : '+ Attach a file'}
            <input type="file" accept="application/pdf,image/png,image/jpeg,text/plain,text/csv" className="hidden" disabled={busy}
              onChange={e => { const f = e.target.files?.[0]; if (f) add(f); e.target.value = ''; }} />
          </label>
        )}
      </div>
    </div>
  );
}

/** The separate download step: choose the language; the person's own is preselected. */
function DownloadChooser({ doc, langs, onClose, onErr }: { doc: DocRow; langs: LangInfo | null; onClose: () => void; onErr: (m: string) => void }) {
  const { views } = useTrViews(doc.id, doc.version, langs);
  const [pick, setPick] = useState<string>(() => preferredLang(langs));
  const opts = (langs?.languages || [{ code: 'en', name: 'English' }]);
  const go = () => {
    const [code, older] = pick.split(':');
    rememberLang(code);
    const q = code === 'en' ? '' : `?lang=${code}${older ? '&older=true' : ''}`;
    downloadPdf(`${R}/docs/${doc.id}/pdf${q}`, `${doc.title}.pdf`, onErr);
    onClose();
  };
  const canGo = (() => {
    const [code, older] = pick.split(':');
    if (code === 'en') return true;
    const v = views[code];
    return !!v && (older ? !!v.older : v.state === 'ready');
  })();
  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-[#1a1d27] border border-gray-700 rounded-xl p-5 w-full max-w-sm" onClick={e => e.stopPropagation()}>
        <div className="text-white font-semibold mb-1">Download “{doc.title}”</div>
        <div className="text-xs text-gray-500 mb-3">Choose the language</div>
        <div className="space-y-1.5">
          {opts.map(l => {
            const v = l.code === 'en' ? null : views[l.code];
            const ready = l.code === 'en' || v?.state === 'ready';
            return (
              <div key={l.code}>
                <label className={`flex items-center gap-2 px-3 py-2 rounded-lg cursor-pointer ${pick === l.code ? 'bg-blue-900/40' : 'hover:bg-gray-800'} ${ready ? '' : 'opacity-60'}`}>
                  <input type="radio" disabled={!ready} checked={pick === l.code} onChange={() => setPick(l.code)} />
                  <span className="text-white">{l.name}</span>
                  <span className="text-xs text-gray-400 ml-auto">
                    {l.code === 'en' ? `v${doc.version}` : !v ? '…' : v.state === 'ready' ? `v${v.source_version}` : v.state === 'failed' ? 'not available' : 'being updated'}
                  </span>
                </label>
                {v && v.state !== 'ready' && (
                  <div className="pl-9 pr-2 text-xs text-amber-300/90 pb-1">
                    {v.state === 'updating' ? `${l.name} of version ${doc.version} is being made (about a minute).` : `${l.name} of version ${doc.version} could not be made — a manager can translate it again.`}
                    {v.older && (
                      <label className="flex items-center gap-2 mt-1 text-gray-300 cursor-pointer">
                        <input type="radio" checked={pick === `${l.code}:older`} onChange={() => setPick(`${l.code}:older`)} />
                        the previous {l.name} (of English v{v.older.source_version})
                      </label>
                    )}
                    <div className="text-gray-500 mt-0.5">or choose English v{doc.version}.</div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
        <div className="flex justify-end gap-2 mt-4">
          <button onClick={onClose} className="px-3 py-1.5 rounded-lg text-gray-300 hover:bg-gray-800 text-sm">Cancel</button>
          <button disabled={!canGo} onClick={go} className="px-4 py-1.5 rounded-lg bg-blue-600 hover:bg-blue-700 disabled:opacity-40 text-white text-sm font-semibold">Download</button>
        </div>
      </div>
    </div>
  );
}

/** Reading on screen, in the chosen language. */
function ReadView({ doc, langs }: { doc: Doc; langs: LangInfo | null }) {
  const [lang, setLang] = useState(() => preferredLang(langs));
  const [older, setOlder] = useState(false);
  const { views } = useTrViews(doc.id, doc.version, langs, true);
  useEffect(() => { setLang(preferredLang(langs)); }, [langs]);
  const v = lang === 'en' ? null : views[lang];
  const shown: { title: string; body: string } | null = lang === 'en' ? { title: doc.title, body: doc.body }
    : v?.state === 'ready' && v.current ? { title: v.current.title, body: v.current.body }
    : older && v?.older ? { title: v.older.title, body: v.older.body } : null;
  const opts = langs?.languages || [];
  return (
    <div className="max-w-3xl">
      {opts.length > 1 && (
        <div className="flex flex-wrap gap-1.5 mb-3">
          {opts.map(l => (
            <button key={l.code} onClick={() => { setLang(l.code); setOlder(false); rememberLang(l.code); }}
              className={`px-3 py-1 rounded-full text-sm ${lang === l.code ? 'bg-blue-600 text-white' : 'bg-gray-800 text-gray-300 hover:bg-gray-700'}`}>{l.name}</button>
          ))}
        </div>
      )}
      {shown ? (
        <>
          {lang !== 'en' && (
            <div className="text-xs text-gray-500 mb-1">
              {langName(langs, lang)} — translated by Claude from English v{older && v?.older ? v.older.source_version : doc.version}
              {older && v?.older ? ` (the current English is v${doc.version})` : ''}
            </div>
          )}
          <div className="bg-white rounded-xl p-6 sm:p-8"><DocView title={shown.title} body={shown.body} light pageStyle={doc.page_style} /></div>
        </>
      ) : (
        <div className="rounded-xl border border-amber-700/60 bg-amber-950/30 p-4 text-sm text-amber-200">
          {!v ? 'Loading…' : v.state === 'updating'
            ? `${langName(langs, lang)} of version ${doc.version} is being made — it usually takes about a minute.`
            : `${langName(langs, lang)} of version ${doc.version} could not be made. A manager can translate it again.`}
          <div className="flex flex-wrap gap-2 mt-3">
            {v?.older && <button onClick={() => setOlder(true)} className="px-3 py-1.5 rounded-lg bg-gray-700 text-white">Show the previous {langName(langs, lang)} (of v{v.older.source_version})</button>}
            <button onClick={() => setLang('en')} className="px-3 py-1.5 rounded-lg bg-gray-700 text-white">Show English v{doc.version}</button>
          </div>
        </div>
      )}
    </div>
  );
}

/** Manager view: the state of every translation of this document, view it, translate it again. */
function TranslationsBox({ doc, langs, onErr }: { doc: Doc; langs: LangInfo | null; onErr: (m: string) => void }) {
  const { views, reload, codes } = useTrViews(doc.id, doc.version, langs, true);
  const [showing, setShowing] = useState<string | null>(null);
  if (!codes.length) return null;
  const again = async (code: string) => {
    if (!window.confirm(`Translate version ${doc.version} into ${langName(langs, code)} again? One paid Claude call (two if the first answer's layout is wrong). The current ${langName(langs, code)} text is replaced.`)) return;
    try { await jfetch(`${R}/docs/${doc.id}/translate/${code}`, { method: 'POST', body: '{}' }); reload(); }
    catch (e: any) { onErr(e.message); }
  };
  const sv = showing ? views[showing] : null;
  return (
    <div className="mt-4 bg-[#1a1d27] border border-gray-800 rounded-xl p-4">
      <div className="text-white font-semibold text-sm mb-1">Translations</div>
      <div className="text-xs text-gray-500 mb-2">Made by Claude from the saved English, once per version, and stored. Edit the English (or the glossary under Languages), not the translation.</div>
      <div className="space-y-1.5 text-sm">
        {codes.map(c => {
          const v = views[c];
          return (
            <div key={c} className="flex flex-wrap items-center gap-3">
              <span className="w-24 text-gray-200">{langName(langs, c)}</span>
              {!v ? <span className="text-gray-500">…</span>
                : v.state === 'ready' ? <span className="text-emerald-300">✓ of v{v.source_version}{v.current ? ` · ${fmtWh(v.current.translated_at, 'datetime')}` : ''}</span>
                : v.state === 'failed' ? <span className="text-red-300" title={v.error || ''}>failed — {v.error}</span>
                : <span className="text-amber-300">translating v{doc.version}…{langs?.api && !langs.api.ready ? ` (waiting: ${langs.api.reason})` : ''}</span>}
              {v?.current && <button onClick={() => setShowing(showing === c ? null : c)} className="text-blue-300 text-xs hover:underline">{showing === c ? 'hide' : 'view'}</button>}
              {v && v.state !== 'updating' && <button onClick={() => again(c)} className="text-amber-300 text-xs hover:underline">translate again</button>}
            </div>
          );
        })}
      </div>
      {sv?.current && <div className="mt-3 bg-white rounded-xl p-5 max-h-96 overflow-auto"><DocView title={sv.current.title} body={sv.current.body} light pageStyle={doc.page_style} /></div>}
    </div>
  );
}

/** Manager section → Languages: which languages exist, the glossary per language, the API state. */
function LanguagesManager({ info, onSaved, onErr }: { info: LangInfo; onSaved: () => void; onErr: (m: string) => void }) {
  const s = info.settings!;
  const [open, setOpen] = useState(false);
  const [sel, setSel] = useState<string[]>(s.languages);
  const [gl, setGl] = useState<Record<string, string>>(s.glossary || {});
  const [busy, setBusy] = useState(false);
  useEffect(() => { setSel(s.languages); setGl(s.glossary || {}); }, [s.languages, s.glossary]);
  const save = async () => {
    setBusy(true);
    try {
      const added = sel.filter(c => !s.languages.includes(c));
      if (added.length) {
        const est = await jfetch(`${R}/languages/estimate`, { method: 'POST', body: JSON.stringify({ languages: added }) });
        if (est.translations && !window.confirm(`Switching on ${added.map(c => langName({ ...info, languages: info.available || [] }, c)).join(', ')} translates ${est.translations} document(s) once — about ${est.translations} paid Claude call(s), a few cents each. Continue?`)) { setBusy(false); return; }
      }
      await jfetch(`${R}/languages`, { method: 'PUT', body: JSON.stringify({ languages: sel, glossary: gl }) });
      onSaved();
    } catch (e: any) { onErr(e.message); } finally { setBusy(false); }
  };
  return (
    <div className="mb-5 rounded-xl border border-emerald-900/60 bg-emerald-950/20 p-3 text-sm">
      <button onClick={() => setOpen(!open)} className="flex items-center gap-2 text-emerald-300 font-semibold">
        {open ? '▾' : '▸'} Languages <span className="text-gray-400 font-normal">— English + {s.languages.map(c => c.toUpperCase()).join(', ') || 'none'}</span>
      </button>
      {open && (
        <div className="mt-3 space-y-3">
          <div className="text-xs text-gray-400">
            Managers write English. Each saved English version is translated by Claude ({info.model}) once per language and stored — reading and downloading never call it.
            {' '}{info.api?.ready ? <span className="text-emerald-300">Claude API ready.</span> : <span className="text-amber-300">Not translating: {info.api?.reason}.</span>}
            {' '}Calls in the last hour: {info.calls_last_hour} of at most {info.cap_per_hour}.
          </div>
          <div className="flex flex-wrap gap-3">
            {(info.available || []).map(l => (
              <label key={l.code} className="flex items-center gap-1.5 text-gray-200 cursor-pointer">
                <input type="checkbox" checked={sel.includes(l.code)} onChange={e => setSel(e.target.checked ? [...sel, l.code] : sel.filter(c => c !== l.code))} /> {l.name}
              </label>
            ))}
          </div>
          {sel.map(c => (
            <div key={c}>
              <div className="text-xs text-gray-400 mb-0.5">Glossary for {(info.available || []).find(l => l.code === c)?.name || c} — one term per line, e.g. <code className="text-gray-200">picking = komplekteerimine</code> (optional; used for every translation)</div>
              <textarea value={gl[c] || ''} onChange={e => setGl({ ...gl, [c]: e.target.value })} rows={3}
                className="w-full bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-gray-100 font-mono text-[12px]" />
            </div>
          ))}
          <button disabled={busy} onClick={save} className="px-4 py-1.5 rounded-lg bg-emerald-700 hover:bg-emerald-600 disabled:opacity-40 text-white">{busy ? 'Saving…' : 'Save languages'}</button>
        </div>
      )}
    </div>
  );
}

// ─── the table editor (2026-10-01): click a table in the preview, edit it as a grid ───────────
// The TEXT stays the one source (the PDF and the translation read it); the editor only reads a
// table's lines into a grid and writes them back. Cells: Enter = a new line in the cell (<br>).

interface TModel { header: boolean; widths: (number | null)[]; head: string[]; rows: { cells: string[]; repeat: number; fill?: boolean }[]; style?: 'grid' | 'lines' }

const decCell = (c: string) => (c || '').replace(/<br\s*\/?>/gi, '\n');
const encCell = (c: string) => (c || '').replace(/\r?\n/g, '<br>').replace(/\|/g, '/').trim();

export function newModel(): TModel {
  return { header: true, widths: [null, null, null, null], head: ['Column 1', 'Column 2', 'Column 3', 'Column 4'],
    rows: [{ cells: ['[ ]', '', '', ''], repeat: 1, fill: true }] };
}

export function modelFromLines(lines: string[]): TModel {
  const ls = lines.map(l => l.trim());
  const header = ls.length > 1 && TABLE_SEP.test(ls[1]) && !TABLE_SEP.test(ls[0]);
  const tb = tableBlock(ls);
  const rows: { cells: string[]; repeat: number }[] = [];
  ls.forEach((ln, i) => {
    if (TABLE_SEP.test(ln) || (header && i === 0)) return;
    const m = ln.match(TABLE_REPEAT);
    const isFill = !!m && m[2].toLowerCase() === 'fill';
    rows.push({ cells: tableCells(m ? m[1] : ln).map(decCell), repeat: m && !isFill ? Math.max(1, Math.min(500, parseInt(m[2], 10))) : 1,
      ...(isFill ? { fill: true } : {}) });
  });
  const n = Math.max(tb.widths.length, header ? tableCells(ls[0]).length : 0, ...rows.map(r => r.cells.length), 1);
  const pad = (r: string[]) => [...r, ...Array(n - r.length).fill('')].slice(0, n);
  return { header, style: tb.style || 'grid', widths: [...tb.widths, ...Array(n).fill(null)].slice(0, n),
    head: header ? pad(tableCells(ls[0]).map(decCell)) : Array(n).fill(''),
    rows: rows.map(r => ({ ...r, cells: pad(r.cells) })) };
}

export function modelToLines(m: TModel): string[] {
  const line = (cells: string[]) => '| ' + cells.map(encCell).join(' | ') + ' |';
  const out: string[] = [];
  const dash = m.style === 'lines' ? '===' : '---';
  const sep = '|' + m.widths.map(w => (w ? `${dash} ${Math.round(w)}%` : dash)).join('|') + '|';
  if (m.header) { out.push(line(m.head)); out.push(sep); }
  else if (m.style === 'lines' || m.widths.some(w => w)) out.push(sep);   // no header: the separator goes first
  for (const r of m.rows) out.push(line(r.cells) + (r.fill ? ' *fill' : r.repeat > 1 ? ` *${r.repeat}` : ''));
  return out;
}

const asBlock = (m: TModel): TableBlock => ({
  header: m.header ? m.head.map(encCell) : null,
  rows: m.rows.flatMap(r => Array(r.fill ? 1 : Math.max(1, r.repeat)).fill(r.cells.map(encCell))),
  fill: (() => { let k = 0; for (const r of m.rows) { if (r.fill) return k; k += Math.max(1, r.repeat); } return null; })(),
  widths: m.widths,
  style: m.style || 'grid',
});

function CellInput({ value, onChange, onFocus, bold }: { value: string; onChange: (v: string) => void; onFocus: (el: HTMLTextAreaElement) => void; bold?: boolean }) {
  const rows = Math.max(1, value.split('\n').length);
  return (
    <textarea value={value} rows={rows} onChange={e => onChange(e.target.value)} onFocus={e => onFocus(e.currentTarget)}
      className={`w-full resize-none bg-transparent outline-none focus:bg-blue-50 px-1.5 py-1 text-[13px] text-gray-900 ${bold ? 'font-bold' : ''} ${value.trim() === '[ ]' || value.trim() === '[x]' ? 'text-center' : ''}`} />
  );
}

function TableEditor({ initial, isNew, onApply, onCancel }: { initial: TModel; isNew: boolean; onApply: (m: TModel | null) => void; onCancel: () => void }) {
  const [m, setM] = useState<TModel>(initial);
  const [focus, setFocus] = useState<{ r: number; c: number } | null>(null);   // r = -1: the header
  const focusEl = React.useRef<HTMLTextAreaElement | null>(null);
  const tableRef = React.useRef<HTMLTableElement>(null);
  const n = m.head.length;
  const eff = tableWidths(asBlock(m));                    // what the PDF will use, in %
  const upd = (f: (x: TModel) => void) => setM(prev => { const x: TModel = JSON.parse(JSON.stringify(prev)); f(x); return x; });
  const setCell = (r: number, c: number, v: string) => upd(x => { if (r < 0) x.head[c] = v; else x.rows[r].cells[c] = v; });
  const addCol = (at: number) => upd(x => { x.head.splice(at, 0, `Column ${x.head.length + 1}`); x.widths.splice(at, 0, null); x.rows.forEach(r => r.cells.splice(at, 0, '')); });
  const delCol = (c: number) => upd(x => { if (x.head.length <= 1) return; x.head.splice(c, 1); x.widths.splice(c, 1); x.rows.forEach(r => r.cells.splice(c, 1)); });
  const moveCol = (c: number, d: number) => upd(x => {
    const j = c + d; if (j < 0 || j >= x.head.length) return;
    const sw = <T,>(a: T[]) => { [a[c], a[j]] = [a[j], a[c]]; };
    sw(x.head); sw(x.widths); x.rows.forEach(r => sw(r.cells));
  });
  const addRow = (at: number) => upd(x => { x.rows.splice(at, 0, { cells: Array(x.head.length).fill(''), repeat: 1 }); });
  const delRow = (r: number) => upd(x => { x.rows.splice(r, 1); });
  const cellText = (f: { r: number; c: number }) => (f.r < 0 ? m.head[f.c] : m.rows[f.r].cells[f.c]);
  /** ☐ at the cursor (a cell with only a box toggles it off) */
  const toggleBox = () => {
    if (!focus) return;
    const cur = cellText(focus);
    if (cur.trim() === '[ ]') { setCell(focus.r, focus.c, ''); return; }
    const el = focusEl.current;
    const at = el ? el.selectionStart : cur.length;
    const lineStart = cur.lastIndexOf('\n', at - 1) + 1;
    setCell(focus.r, focus.c, cur.slice(0, lineStart) + '[ ] ' + cur.slice(lineStart));
  };
  /** the selected words (or the cursor's line) become small grey text */
  const smallText = () => {
    if (!focus) return;
    const cur = cellText(focus);
    const el = focusEl.current;
    let a = el ? el.selectionStart : 0, b = el ? el.selectionEnd : cur.length;
    if (a === b) { a = cur.lastIndexOf('\n', a - 1) + 1; const e = cur.indexOf('\n', b); b = e < 0 ? cur.length : e; }
    if (a === b) return;
    setCell(focus.r, focus.c, `${cur.slice(0, a)}<small>${cur.slice(a, b)}</small>${cur.slice(b)}`);
  };
  // drag a column's right edge: every column is fixed at its current width, then this one and its
  // right-hand neighbour trade width
  const startDrag = (c: number, e: React.PointerEvent) => {
    if (c >= n - 1 || !tableRef.current) return;
    e.preventDefault();
    const total = tableRef.current.getBoundingClientRect().width * 0.88;   // the data columns' share
    const start = eff.map(w => Math.round(w));
    const x0 = e.clientX;
    const move = (ev: PointerEvent) => {
      const d = ((ev.clientX - x0) / total) * 100;
      const a = Math.max(3, Math.min(start[c] + start[c + 1] - 3, start[c] + d));
      const ws = [...start];
      ws[c] = Math.round(a); ws[c + 1] = start[c] + start[c + 1] - ws[c];
      setM(prev => ({ ...prev, widths: ws }));
    };
    const up = () => { window.removeEventListener('pointermove', move); window.removeEventListener('pointerup', up); };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
  };
  const fixedAny = m.widths.some(w => w);

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-start justify-center p-2 sm:p-6 overflow-auto" onClick={onCancel}>
      <div className="bg-[#1a1d27] border border-gray-700 rounded-xl p-4 w-full max-w-6xl" onClick={e => e.stopPropagation()}>
        <div className="flex flex-wrap items-center gap-2 mb-3">
          <div className="text-white font-semibold mr-2">{isNew ? 'New table' : 'Edit table'}</div>
          <label className="text-xs text-gray-300 flex items-center gap-1 cursor-pointer">
            <input type="checkbox" checked={m.header} onChange={e => upd(x => { x.header = e.target.checked; })} /> Header row (repeats on every PDF page)
          </label>
          <label className="text-xs text-gray-300 flex items-center gap-1">Style
            <select value={m.style || 'grid'} onChange={e => upd(x => { x.style = e.target.value as 'grid' | 'lines'; })}
              className="bg-gray-800 border border-gray-600 rounded px-1 py-0.5 text-white">
              <option value="grid">Grid — every cell boxed</option>
              <option value="lines">Lines — a line under each row</option>
            </select></label>
          <span className="flex-1" />
          <button onMouseDown={e => e.preventDefault()} onClick={toggleBox} disabled={!focus} title="Put a ☐ at the start of the cursor's line in the cell (a cell with only ☐: take it out)"
            className="px-2.5 py-1 rounded-lg bg-gray-700 hover:bg-gray-600 disabled:opacity-40 text-white text-xs">☐ in cell</button>
          <button onMouseDown={e => e.preventDefault()} onClick={smallText} disabled={!focus} title="Make the selected words (or the cursor's line) small grey text — for notes"
            className="px-2.5 py-1 rounded-lg bg-gray-700 hover:bg-gray-600 disabled:opacity-40 text-white text-xs"><small>small</small> text</button>
          <button onClick={() => addCol(n)} className="px-2.5 py-1 rounded-lg bg-gray-700 hover:bg-gray-600 text-white text-xs">+ Column</button>
          <button onClick={() => addRow(m.rows.length)} className="px-2.5 py-1 rounded-lg bg-gray-700 hover:bg-gray-600 text-white text-xs">+ Row</button>
          {fixedAny && <button onClick={() => upd(x => { x.widths = x.widths.map(() => null); })} className="px-2.5 py-1 rounded-lg bg-gray-700 hover:bg-gray-600 text-white text-xs">Automatic widths</button>}
        </div>
        <div className="text-xs text-gray-400 mb-2">
          Click a cell and type — Enter makes a new line inside the cell. Drag a column's right edge to make it wider or narrower.
          A row's <b>×</b> number repeats it; <b>fill page</b> repeats it to the end of the PDF page instead, so no space is wasted (one row per table).
        </div>
        <div>
          <div className="bg-white rounded-lg p-2 overflow-x-auto">
            <table ref={tableRef} className="w-full table-fixed border-collapse min-w-[32rem]">
              <colgroup>{eff.map((w, i) => <col key={i} style={{ width: `${w * 0.88}%` }} />)}<col style={{ width: '12%' }} /></colgroup>
              <thead>
                <tr>{m.head.map((_, c) => (
                  <th key={c} className="p-0 pb-1 font-normal">
                    <div className="flex items-center justify-between gap-1 text-[10px] text-gray-500 px-1">
                      <span>
                        <button onClick={() => moveCol(c, -1)} disabled={c === 0} className="px-0.5 disabled:opacity-30" title="Move left">◀</button>
                        <button onClick={() => moveCol(c, 1)} disabled={c === n - 1} className="px-0.5 disabled:opacity-30" title="Move right">▶</button>
                      </span>
                      <span title={m.widths[c] ? 'width set by you' : 'automatic width'}>{Math.round(eff[c])}%{m.widths[c] ? '' : ' auto'}</span>
                      <span>
                        <button onClick={() => addCol(c + 1)} className="px-0.5" title="Insert a column to the right">＋</button>
                        <button onClick={() => delCol(c)} disabled={n <= 1} className="px-0.5 text-red-500 disabled:opacity-30" title="Delete this column">✕</button>
                      </span>
                    </div>
                  </th>))}<th /></tr>
                {m.header && <tr>{m.head.map((v, c) => (
                  <th key={c} className="relative border border-gray-500 bg-gray-100 p-0 align-top">
                    <CellInput value={v} bold onChange={x => setCell(-1, c, x)} onFocus={el => { focusEl.current = el; setFocus({ r: -1, c }); }} />
                    {c < n - 1 && <span onPointerDown={e => startDrag(c, e)} title="Drag to change the width"
                      className="absolute top-0 -right-1.5 w-3 h-full cursor-col-resize z-10 hover:bg-blue-400/40" />}
                  </th>))}<th className="text-[10px] text-gray-400 font-normal align-bottom pl-1 text-left">header</th></tr>}
              </thead>
              <tbody>{m.rows.map((r, ri) => (
                <tr key={ri}>{r.cells.map((v, c) => (
                  <td key={c} className={`relative ${m.style === 'lines' ? 'border-b border-gray-600 border-x border-x-gray-200' : 'border border-gray-500'} p-0 align-top`}>
                    <CellInput value={v} onChange={x => setCell(ri, c, x)} onFocus={el => { focusEl.current = el; setFocus({ r: ri, c }); }} />
                    {!m.header && ri === 0 && c < n - 1 && <span onPointerDown={e => startDrag(c, e)} title="Drag to change the width"
                      className="absolute top-0 -right-1.5 w-3 h-full cursor-col-resize z-10 hover:bg-blue-400/40" />}
                  </td>))}
                  <td className="pl-1 align-top">
                    <div className="flex flex-wrap items-center gap-0.5 text-xs text-gray-600 pt-1">
                      <span title="How many times this row repeats">×</span>
                      <input type="number" min={1} max={500} value={r.repeat} disabled={!!r.fill}
                        onChange={e => upd(x => { x.rows[ri].repeat = Math.max(1, Math.min(500, Number(e.target.value) || 1)); })}
                        className="w-12 border border-gray-300 rounded px-1 py-0 text-gray-900 disabled:opacity-30" />
                      <label className="flex items-center gap-0.5 cursor-pointer whitespace-nowrap" title="Repeat this row until the PDF page is full">
                        <input type="checkbox" checked={!!r.fill} onChange={e => upd(x => { x.rows.forEach((y, j) => { y.fill = j === ri ? e.target.checked : false; }); })} /> fill page</label>
                      <button onClick={() => addRow(ri + 1)} className="px-0.5 hover:text-black" title="Insert a row below">＋</button>
                      <button onClick={() => delRow(ri)} className="px-0.5 text-red-500" title="Delete this row">✕</button>
                    </div>
                  </td></tr>))}
              </tbody>
            </table>
          </div>
        </div>
        <div className="flex flex-wrap justify-end gap-2 mt-4">
          {!isNew && <button onClick={() => { if (window.confirm('Remove this table from the text?')) onApply(null); }} className="px-3 py-1.5 rounded-lg text-red-300 hover:bg-red-950/40 text-sm mr-auto">Delete table</button>}
          <button onClick={onCancel} className="px-3 py-1.5 rounded-lg text-gray-300 hover:bg-gray-800 text-sm">Cancel</button>
          <button onClick={() => onApply(m)} disabled={!m.rows.length && !m.header} className="px-4 py-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-500 disabled:opacity-40 text-white text-sm font-semibold">
            {isNew ? 'Insert table' : 'Apply'}</button>
        </div>
        <div className="text-[11px] text-gray-500 mt-1 text-right">Apply puts the table into the text — the document is saved with Save, as always.</div>
      </div>
    </div>
  );
}

// ─── the editor's toolbar (2026-10-01): every element one click away — it writes the text ──────

type Edit = (v: string, a: number, b: number) => { v: string; a: number; b: number };

function EditorToolbar({ area, body, setBody, onTable }: { area: React.RefObject<HTMLTextAreaElement | null>; body: string; setBody: (v: string) => void; onTable: () => void }) {
  const apply = (f: Edit) => {
    const el = area.current;
    const a0 = el ? el.selectionStart : body.length, b0 = el ? el.selectionEnd : body.length;
    const r = f(body, a0, b0);
    setBody(r.v);
    requestAnimationFrame(() => { const e = area.current; if (e) { e.focus(); e.setSelectionRange(r.a, r.b); } });
  };
  /** around the selection (or a placeholder word, selected so typing replaces it) */
  const wrap = (open: string, close: string, ph: string) => apply((v, a, b) => {
    const sel = v.slice(a, b) || ph;
    return { v: v.slice(0, a) + open + sel + close + v.slice(b), a: a + open.length, b: a + open.length + sel.length };
  });
  /** a marker at the start of every selected line (numbered steps count up) */
  const lines = (mark: (i: number) => string, strip: RegExp) => apply((v, a, b) => {
    const s0 = v.lastIndexOf('\n', a - 1) + 1;
    const e0 = (() => { const e = v.indexOf('\n', Math.max(b - (b > a && v[b - 1] === '\n' ? 1 : 0), a)); return e < 0 ? v.length : e; })();
    const out = v.slice(s0, e0).split('\n').map((ln, i) => mark(i) + ln.replace(strip, ''));
    const txt = out.join('\n');
    return { v: v.slice(0, s0) + txt + v.slice(e0), a: s0 + txt.length, b: s0 + txt.length };
  });
  const LIST = /^(#{1,2} |[-*•] |\d{1,3}[.)]\s+|\[[ xX]\]\s+|>\s?)/;
  /** a block on its own line(s) at the cursor, with the given part selected */
  const block = (text: string, selFrom = 0, selTo = 0) => apply((v, a) => {
    const atStart = a === 0 || v[a - 1] === '\n';
    const pre = atStart ? '' : '\n';
    const post = v[a] === '\n' || a >= v.length ? '' : '\n';
    const at = a + pre.length;
    return { v: v.slice(0, a) + pre + text + post + v.slice(a), a: at + selFrom, b: at + (selTo || text.length) };
  });
  const B = ({ t, title, on, children }: { t?: string; title: string; on: () => void; children?: React.ReactNode }) => (
    <button type="button" title={title} onMouseDown={e => e.preventDefault()} onClick={on}
      className="min-w-[2rem] px-2 py-1 rounded bg-gray-700 hover:bg-gray-600 text-white text-xs">{children ?? t}</button>
  );
  return (
    <div className="flex flex-wrap items-center gap-1">
      <B t="H1" title="Heading" on={() => lines(() => '# ', LIST)} />
      <B t="H2" title="Sub-heading" on={() => lines(() => '## ', LIST)} />
      <B title="Bold — the selected words" on={() => wrap('**', '**', 'bold text')}><b>B</b></B>
      <B title="Small grey text — for notes under a line" on={() => wrap('<small>', '</small>', 'small note')}><small>small</small></B>
      <span className="w-px h-5 bg-gray-700 mx-1" />
      <B t="• List" title="Bullet list — every selected line" on={() => lines(() => '- ', LIST)} />
      <B t="1. Steps" title="Numbered steps — every selected line" on={() => lines(i => `${i + 1}. `, LIST)} />
      <B t="☐ Checklist" title="Checklist — a box in front of every selected line" on={() => lines(() => '[ ] ', LIST)} />
      <B t="⚠ Warning" title="Yellow warning box — every selected line" on={() => lines(() => '> ', LIST)} />
      <span className="w-px h-5 bg-gray-700 mx-1" />
      <B t="___ Fill-in line" title="A label with a line to write on, e.g. Date: ____ — an optional (note) after it" on={() => block('Label: ___', 0, 5)} />
      <B t="─ Line" title="A divider line across the page" on={() => block('---')} />
      <B t="═ Double" title="A double divider line" on={() => block('===')} />
      <B t="▦ Table" title="Insert a table (click a table in the preview to edit it)" on={onTable} />
    </div>
  );
}

/** The editor's A4 view: the real PDF pages, made by the server from the unsaved text. */
function A4View({ title, body, pageStyle }: { title: string; body: string; pageStyle: PageStyle }) {
  const [open, setOpen] = useState<boolean>(() => { try { return localStorage.getItem('wms_instr_a4') !== '0'; } catch { return true; } });
  const [pages, setPages] = useState<{ pages: string[]; count: number } | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const key = JSON.stringify({ title, body, pageStyle });
  useEffect(() => {
    if (!open) return;
    let gone = false;
    const t = setTimeout(async () => {
      setBusy(true);
      try {
        const d = await jfetch(`${R}/preview-pages`, { method: 'POST', body: JSON.stringify({ title, body, page_style: pageStyle }) });
        if (!gone) { setPages(d); setErr(''); }
      } catch (e: any) { if (!gone) setErr(e.message); } finally { if (!gone) setBusy(false); }
    }, 1500);
    return () => { gone = true; clearTimeout(t); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, open]);
  const toggle = () => { const v = !open; setOpen(v); try { localStorage.setItem('wms_instr_a4', v ? '1' : '0'); } catch { /* */ } };
  return (
    <div className="mt-5">
      <div className="flex items-center gap-3 mb-2">
        <button onClick={toggle} className="text-sm text-white font-semibold">{open ? '▾' : '▸'} A4 view — exactly what prints</button>
        {open && <span className="text-xs text-gray-500">{busy ? 'updating…' : pages ? `${pages.count} page${pages.count === 1 ? '' : 's'}${pages.count > pages.pages.length ? ` (first ${pages.pages.length} shown)` : ''} · updates when you stop typing` : ''}</span>}
        {err && <span className="text-xs text-red-300">{err}</span>}
      </div>
      {open && pages && (
        <div className={`flex flex-wrap gap-6 justify-center bg-gray-800/60 rounded-xl p-6 ${busy ? 'opacity-70' : ''}`}>
          {pages.pages.map((src, i) => (
            <div key={i} className="text-center">
              <img src={src} alt={`page ${i + 1}`} className="bg-white shadow-2xl w-[min(100%,560px)] aspect-[210/297]" />
              <div className="text-[11px] text-gray-400 mt-1">page {i + 1} / {pages.count}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
